import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useShop } from '@/hooks/useShop';

const initialsOf = (name) =>
	(name ?? 'Your shop')
		.split(' ')
		.map((part) => part[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();

/**
 * Multi-location shop switcher. Ported from the Lovable `StoreSwitcher`
 * (nearst/fresh-dashboard) onto our real shop data and component library.
 *
 * `pill` is the expanded-sidebar card; `avatar` is the collapsed-sidebar
 * circle. Only rendered for multi-shop orgs, so the URL is always
 * shop-scoped. Switching keeps the user on the equivalent page for the new
 * shop (e.g. Inventory stays on Inventory) rather than dumping them at its
 * root, with two exceptions: the per-shop product-detail route falls back
 * to the manage list (a barcode belongs to one shop), and `/onboarding` —
 * a per-shop setup gate — resets to the new shop's root so a completed
 * shop never lands back on it (NEA-5772). The `/:shopId` layout then
 * resolves the new shop's dashboard / onboarding state.
 */
export function StoreSwitcher({ variant = 'pill' }) {
	const { shop, shops } = useShop();
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = useState(false);

	const activeName = shop?.name ?? 'Your shop';
	const activeLocation = shop?.city ?? null;
	const initials = initialsOf(shop?.name);

	const onSelect = (shopId) => {
		setOpen(false);
		if (shopId === shop?.id) return;

		const prefix = shop?.id ? `/${shop.id}` : null;
		const scoped = prefix && (location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
		const currentSub = scoped ? location.pathname.slice(prefix.length) : '';
		// Onboarding is a per-shop setup gate, not a normal page — carrying it
		// to the new shop is what trapped chain users (NEA-5772). Reset to the
		// new shop's root so AppLayout re-decides: a completed shop lands on its
		// home, one that still needs setup is redirected back into onboarding.
		// The route stays reachable for a deliberate visit (changing POS/GBP)
		// because the page never redirects away — so the "don't get stuck"
		// logic lives here, in the switcher. Product detail is per-shop too, so
		// the new shop wouldn't have that barcode — fall back to the manage list.
		const onboarding = /^\/onboarding(\/|$)/.test(currentSub);
		const sub = onboarding ? '' : currentSub.replace(/^\/inventory\/manage\/[^/]+$/, '/inventory/manage');
		navigate(`/${shopId}${sub}${scoped && !onboarding ? location.search : ''}`);
	};

	const trigger =
		variant === 'avatar' ? (
			<button
				type="button"
				aria-label={activeName}
				className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white"
			>
				{initials}
			</button>
		) : (
			<button
				type="button"
				className="group mx-4 mb-6 mt-7 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl border border-sidebar-border bg-white px-3 py-2.5 text-left transition-colors hover:bg-accent"
			>
				<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
					{initials}
				</div>
				<div className="min-w-0 flex-1">
					<div className="truncate text-sm font-semibold text-foreground">{activeName}</div>
					{activeLocation ? <div className="truncate text-[11px] text-muted-foreground">{activeLocation}</div> : null}
				</div>
				<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
			</button>
		);

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
			<DropdownMenuContent
				align={variant === 'avatar' ? 'start' : 'center'}
				side={variant === 'avatar' ? 'right' : 'bottom'}
				sideOffset={8}
				className="w-56 overflow-hidden rounded-xl p-0"
			>
				<div className="py-1.5">
					{shops.map((s) => {
						const isActive = s.id === shop?.id;
						return (
							<button
								key={s.id}
								type="button"
								onClick={() => onSelect(s.id)}
								className={cn(
									'flex w-full items-center gap-3 px-3 py-2 transition-colors',
									isActive ? 'bg-blue-50/60' : 'hover:bg-muted/60'
								)}
							>
								<span className={cn('h-2 w-2 rounded-full', isActive ? 'bg-blue-600' : 'bg-muted-foreground/30')} />
								<span
									className={cn(
										'flex-1 truncate text-left text-[13px]',
										isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
									)}
								>
									{s.name}
								</span>
								{isActive ? <Check className="h-4 w-4 text-blue-600" strokeWidth={2.5} /> : null}
							</button>
						);
					})}
				</div>
				<div className="border-t border-border/60 bg-muted/30">
					<button
						type="button"
						onClick={() => {
							setOpen(false);
							navigate(shop?.id ? `/${shop.id}/settings` : '/settings');
						}}
						className="w-full px-3 py-2.5 text-left text-[12px] font-medium text-blue-600 transition-colors hover:text-blue-700"
					>
						Manage stores
					</button>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
